import { css } from '@emotion/core';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons/faSpinner';
import { Icon } from '@veeva/corgix/icon';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import useMessageService from '../../services/i18n/useMessageService';
import NavigationTabs from '../../constants/NavigationTabs';
import { AuthenticatedPaths } from '../../constants/Paths';

const propTypes = {
    children: PropTypes.node,
    childrenLoading: PropTypes.bool,
    contentContainerCSS: PropTypes.shape({}),
    headerDescription: PropTypes.node,
    selectedTab: PropTypes.oneOf([
        NavigationTabs.HOME,
        NavigationTabs.SYSTEMS,
        NavigationTabs.STUDIES,
        NavigationTabs.GENERAL,
        NavigationTabs.EMAILS,
        NavigationTabs.CHANGE_PASSWORD,
        NavigationTabs.RECENT_ACTIVITY,
    ]),
};

const MyAccountLayoutDesktop = ({
    children,
    childrenLoading,
    contentContainerCSS,
    headerDescription,
    selectedTab,
}) => {
    const [[myAccountLabel, generalLabel, emailsLabel, changePasswordLabel, recentActivityLabel]] =
        useMessageService([
            'my_account.my_account',
            'my_account.general',
            'my_account.emails',
            'my_account.change_password',
            'my_account.recent_activity',
        ]);

    const tabLabels = {
        [NavigationTabs.GENERAL]: generalLabel,
        [NavigationTabs.EMAILS]: emailsLabel,
        [NavigationTabs.CHANGE_PASSWORD]: changePasswordLabel,
        [NavigationTabs.RECENT_ACTIVITY]: recentActivityLabel,
        [NavigationTabs.HOME]: myAccountLabel,
    };

    const pageTitleCSS = css`
        font-size: 2rem;
        color: #303030;
        padding-bottom: 3.333rem;
        text-align: center;
        font-weight: bold;
    `;

    const contentCSS = css`
        background-color: #ffffff;
        padding: 0;
        box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.4);
        border-radius: 8px;
        margin: auto;
        display: flex;
        flex-direction: row;
        flex-grow: 1;
        min-height: 17.333rem; // height of the side tabs container
        ${contentContainerCSS}
    `;

    const Tab = ({ tab, path, label }) => {
        return (
            <li
                css={css`
                    margin-bottom: 1.333rem;
                    outline: none;
                `}
            >
                <Link
                    css={css`
                        color: ${selectedTab === tab ? '#f8972b' : '#303030'};
                        font-weight: ${selectedTab === tab ? 'bold' : undefined};
                        text-decoration: none;
                        &:hover,
                        &:focus {
                            color: #f8972b;
                            outline: none;
                        }
                    `}
                    to={path}
                >
                    {label}
                </Link>
            </li>
        );
    };

    return (
        <div
            css={css`
                display: flex;
                flex-direction: column;
                min-width: 58.333rem;
                padding: 3.333rem;
                font-size: 1.167rem;
            `}
        >
            <div css={pageTitleCSS}>{myAccountLabel}</div>
            <div css={contentCSS}>
                <div
                    css={css`
                        width: 16.667rem;
                        border-right: 1px solid #d9d9d9;
                        box-sizing: border-box;
                    `}
                >
                    <ul
                        css={css`
                            padding: 3.333rem 1.333rem 3.333rem 3.333rem;
                            list-style: none;
                            margin: 0;
                        `}
                    >
                        <Tab
                            path={AuthenticatedPaths.MY_ACCOUNT}
                            label={generalLabel}
                            tab={NavigationTabs.GENERAL}
                        />
                        <Tab
                            path={AuthenticatedPaths.EMAILS}
                            label={emailsLabel}
                            tab={NavigationTabs.EMAILS}
                        />
                        <Tab
                            path={AuthenticatedPaths.CHANGE_PASSWORD}
                            label={changePasswordLabel}
                            tab={NavigationTabs.CHANGE_PASSWORD}
                        />
                        <Tab
                            path={AuthenticatedPaths.RECENT_ACTIVITY}
                            label={recentActivityLabel}
                            tab={NavigationTabs.RECENT_ACTIVITY}
                        />
                    </ul>
                </div>
                {childrenLoading ? (
                    <div
                        css={css`
                            align-items: center;
                            display: flex;
                            justify-content: center;
                            width: 41.6667rem;
                        `}
                    >
                        <Icon spin type={faSpinner} color="#555555" size="xl" />
                    </div>
                ) : (
                    <div
                        css={css`
                            box-sizing: border-box;
                            display: flex;
                            flex-direction: column;
                            min-width: 41.6667rem;
                            padding: 3.333rem;
                        `}
                    >
                        <div
                            css={css`
                                display: flex;
                                flex-direction: column;
                                gap: 0.3333rem;
                                margin-bottom: 1.6667rem;
                            `}
                        >
                            <header
                                css={css`
                                    font-size: 1.3333rem;
                                    font-weight: bold;
                                `}
                            >
                                {tabLabels[selectedTab]}
                            </header>
                            {headerDescription}
                        </div>
                        {children}
                    </div>
                )}
            </div>
        </div>
    );
};

MyAccountLayoutDesktop.propTypes = propTypes;

export default MyAccountLayoutDesktop;
